import React from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import Tilt from "react-parallax-tilt";
import { AiFillGithub, AiFillMail } from "react-icons/ai";
import {
  FaLinkedinIn,
  FaHackerrank,
  FaDownload,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { SiReadthedocs } from "react-icons/si";

// Importing PDFs from the Assets folder
import goVsJavaPdf from "../Assets/Go vs. Java A Detailed Performance Analysis in Concurrent Programming.pdf";
import aafScmPdf from "../Assets/An Authenticated Framework for Supply Chain Management.pdf";
import webToMobilePdf from "../Assets/Comparative Analysis of Web-to-Mobile App Conversion.pdf";
import NltoSql from "../Assets/An AI-Driven Natural Language to SQL Framework in Go Leveraging Large Language Models and Dynamic Schema Contextualization.pdf";

function Research() {
  return (
    <Container fluid className="home-about-section" id="about">
      <Container>
        <Row>
          <Col md={8} className="home-about-description">
            <h1
              style={{ fontSize: "2.6em" }}
              className="text-decoration-underline"
            >
              Technical <span className="purple">Publications </span>
            </h1>

            <div className="home-about-body">
              {/* Paper 4: AI-Driven NL to SQL Framework */}
              <div
                className="research-item mb-5 p-3"
                style={{
                  borderLeft: "3px solid #c770fe",
                  background: "rgba(199, 112, 254, 0.05)",
                }}
              >
                <p className="fw-bold purple" style={{ fontSize: "1.3em" }}>
                  An AI-Driven Natural Language to SQL Framework in Go
                  Leveraging Large Language Models and Dynamic Schema
                  Contextualization
                </p>
                <p style={{ textAlign: "justify" }}>
                  The need to know is compounded further when the volume of data
                  held in an organization grows, since non-technical users are
                  then not allowed to have fairly instant access to their
                  solution by an obscure barrier to the answer in the form of
                  SQL. To bridge that gap in a given middleware architecture,
                  this paper presents an NLP-to-SQL Query Executor. We come up
                  with a system of Go backend that integrates with the Gemini
                  2.5-flash LLM to translate the high-level natural language
                  queries into executable SQL queries in real-time. This project
                  is novel because it has a Dynamic Schema Injection (DSI).
                  Rather than a fixed set of regular expressions as we used to
                  see in the past, this engine programmatically accesses
                  database schema information to construct a contextually
                  correct "World Model" of the database - e.g. table
                  relationships and column metadata - and then loads it into the
                  system instructions of the LLM. This offers a mechanism to
                  obtain high-quality query results whilst the format of the
                  underlying database changes. This project has a
                  JSON-schema-based structured output to promote sanity in the
                  generated SQL, a custom CORS-enabled REST API in front of it,
                  and a responsive frontend previewing results in tabular form
                  immediately. We empirically test the three components and
                  demonstrate how the components could effectively support ad
                  hoc queries like product information and price comparison, and
                  do so with low latency.
                </p>
                <Button
                  href="https://ieeexplore.ieee.org/document/11485070"
                  target="_blank"
                  className="fork-btn-inner m-1"
                >
                  <FaExternalLinkAlt /> &nbsp; IEEE Xplore
                </Button>
                <Button
                  href={NltoSql}
                  download="An AI-Driven Natural Language to SQL
Framework in Go Leveraging Large
Language Models and Dynamic Schema
Contextualization.pdf"
                  className="fork-btn-inner m-1"
                >
                  <FaDownload /> &nbsp; Download Paper
                </Button>
              </div>

              {/* Paper 3: Web-to-Mobile Conversion */}
              <div
                className="research-item mb-5 p-3"
                style={{
                  borderLeft: "3px solid #c770fe",
                  background: "rgba(199, 112, 254, 0.05)",
                }}
              >
                <p className="fw-bold purple" style={{ fontSize: "1.3em" }}>
                  Comparative Analysis of Web-to-Mobile App Conversion:
                  Leveraging Nuxt, Capacitor and Java
                </p>
                <p style={{ textAlign: "justify" }}>
                  Mobile app development has advanced significantly, with
                  developers navigating the trade-offs between native and
                  cross-platform approaches to meet diverse user requirements.
                  This study investigates the comparative strengths of Java, a
                  robust and widely used language for native Android
                  development, and Capacitor, a modern cross-platform framework
                  utilizing web technologies. By developing identical
                  applications that integrate device-specific features and
                  API-driven movie data, the research evaluates key metrics such
                  as performance, memory usage, development time, and cost
                  efficiency. The findings reveal that Java excels in-memory
                  optimization, deep native API access, and high-performance
                  capabilities, making it ideal for Android-centric applications
                  requiring precision and efficiency, albeit at the cost of
                  increased development complexity. Capacitor, in contrast,
                  simplifies cross-platform deployment by leveraging web
                  technologies and modular plugins, enabling faster development
                  cycles and reduced costs. Its rapid execution times and ease
                  of scaling highlight its suitability for multi-platform
                  solutions. This research offers practical guidance for
                  developers and organizations to select the appropriate
                  technology stack based on project goals, team expertise, and
                  budget constraints, fostering informed decision-making in
                  modern mobile app development strategies.
                </p>
                <Button
                  href="https://ieeexplore.ieee.org/document/11088910"
                  target="_blank"
                  className="fork-btn-inner m-1"
                >
                  <FaExternalLinkAlt /> &nbsp; IEEE Xplore
                </Button>
                <Button
                  href={webToMobilePdf}
                  download="Web_to_Mobile_App_Conversion.pdf"
                  className="fork-btn-inner m-1"
                >
                  <FaDownload /> &nbsp; Download Paper
                </Button>
              </div>

              {/* Paper 1: Go vs Java */}
              <div
                className="research-item mb-5 p-3"
                style={{
                  borderLeft: "3px solid #c770fe",
                  background: "rgba(199, 112, 254, 0.05)",
                }}
              >
                <p className="fw-bold purple" style={{ fontSize: "1.3em" }}>
                  Go vs. Java: A Detailed Performance Analysis in Concurrent
                  Programming
                </p>
                <p style={{ textAlign: "justify" }}>
                  Go, a programming language introduced by Google, represents a
                  relatively recent advancement in the world of software
                  development. Designed to support modern programming needs, it
                  combines robust functionality with a strong emphasis on
                  concurrency and efficient development workflows. While it
                  shares some conceptual similarities with Java, Go
                  distinguishes itself with unique features. This study
                  investigates the comparative performance of Go and Java, with
                  a specific focus on compile time and concurrency—a critical
                  area in today's landscape where applications must manage
                  simultaneous data processing with precision. Despite frequent
                  comparisons between programming languages in academic
                  research, detailed evaluations of Go remain scarce,
                  particularly as earlier studies were conducted during its
                  nascent stages. This paper aims to fill that gap by providing
                  a comprehensive analysis of how Go and Java handle
                  concurrency, evaluating runtime performance, memory
                  efficiency, and their distinctive concurrency models. To
                  explore their concurrency mechanisms, CRUD operations were
                  implemented on a MySQL database using programs written in both
                  languages. Go employs Goroutines for lightweight concurrency,
                  whereas Java relies on its traditional threading model with
                  Java Threads. Each program was executed a thousand times, and
                  the results were subjected to statistical analysis and
                  graphically represented to provide a thorough and visual
                  comparison of the two languages' performance.
                </p>
                <Button
                  href="https://ieeexplore.ieee.org/document/10894767"
                  target="_blank"
                  className="fork-btn-inner m-1"
                >
                  <FaExternalLinkAlt /> &nbsp; IEEE Xplore
                </Button>
                <Button
                  href={goVsJavaPdf}
                  download="Go_vs_Java_Performance_Analysis.pdf"
                  className="fork-btn-inner m-1"
                >
                  <FaDownload /> &nbsp; Download Paper
                </Button>
              </div>

              {/* Paper 2: AAF-SCM */}
              <div
                className="research-item mb-5 p-3"
                style={{
                  borderLeft: "3px solid #c770fe",
                  background: "rgba(199, 112, 254, 0.05)",
                }}
              >
                <p className="fw-bold purple" style={{ fontSize: "1.3em" }}>
                  AAF-SCM: An Authenticated Framework for Supply Chain
                  Management
                </p>
                <p style={{ textAlign: "justify" }}>
                  The process of a supply chain involves a series of activities
                  that distribute, assemble, and manage products, transferring
                  them from the supplier to the end consumer. It operates as a
                  complex, interconnected network regulated by supply and
                  demand. Security in supply chain management is a crucial
                  aspect of its overall security measures, focusing primarily on
                  maintaining essential end-to-end protection. Without this,
                  several attacks can be used to manipulate data and valuable
                  information from the entire chain of networks. This paper
                  explains how encrypted intermediates can secure the entire SCM
                  by validating each intermediate using public-private key
                  encryption. We also demonstrate and assess how the proposed
                  AAF-SCM model can aid in tracing security breaches or
                  unauthorized data tampering. We also validate this model
                  against nefarious attacks like Fabrication, Assertion,
                  Deletion, and Modification. Finally, we propose opportunities
                  to develop innovative systems that enhance security through
                  intermediary steps.
                </p>
                <Button
                  href="https://ieeexplore.ieee.org/document/10829139"
                  target="_blank"
                  className="fork-btn-inner m-1"
                >
                  <FaExternalLinkAlt /> &nbsp; IEEE Xplore
                </Button>
                <Button
                  href={aafScmPdf}
                  download="AAF_SCM_Framework.pdf"
                  className="fork-btn-inner m-1"
                >
                  <FaDownload /> &nbsp; Download Paper
                </Button>
              </div>
            </div>
          </Col>

          <Col md={4} className="myAvtar" style={{ paddingTop: "80px" }}>
            <Tilt>
              <SiReadthedocs style={{ fontSize: "18em", color: "#c770fe" }} />
            </Tilt>
          </Col>
        </Row>

        <Row>
          <Col md={12} className="home-about-social">
            <h1>FIND ME ON</h1>
            <p>
              Feel free to <span className="purple">connect </span>with me
            </p>
            <ul className="home-about-social-links">
              <li className="social-icons">
                <a
                  href="https://github.com/NeazMorshed180031"
                  target="_blank"
                  rel="noreferrer"
                  className="icon-colour home-social-icons"
                >
                  <AiFillGithub />
                </a>
              </li>
              <li className="social-icons">
                <a
                  href="https://www.hackerrank.com/neazmorshed_sd?hr_r=1"
                  target="_blank"
                  rel="noreferrer"
                  className="icon-colour home-social-icons"
                >
                  <FaHackerrank />
                </a>
              </li>
              <li className="social-icons">
                <a
                  href="https://www.linkedin.com/in/neazmorshedsd/"
                  target="_blank"
                  rel="noreferrer"
                  className="icon-colour home-social-icons"
                >
                  <FaLinkedinIn />
                </a>
              </li>
              <li className="social-icons">
                <a
                  href="mailto:neazmorshed180031@gmail.com"
                  target="_blank"
                  rel="noreferrer"
                  className="icon-colour home-social-icons"
                >
                  <AiFillMail />
                </a>
              </li>
            </ul>
          </Col>
        </Row>
      </Container>
    </Container>
  );
}

export default Research;
